import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2, RotateCcw, ShieldCheck, X } from 'lucide-react';
import ConfirmModal from '../shared/ConfirmModal';
import EmployeePermissions from './EmployeePermissions';
import {
  EmployeeDraft, PERMISSION_ROLES, PermissionDef, RoleOption, StoreEmployee, StoreFlags,
  buildPayload, currentPinLength, errorKeyForCode, fullName, initialDraft, isLegacyRole,
  offeredRoles, permissionView, pinLengthFor, pinMismatch,
} from './employeeModel';

export interface PosTerminalOption {
  id: number;
  terminal_id: string;
  terminal_name?: string | null;
  is_active?: boolean;
}

/** Σφάλμα αποθήκευσης όπως το δίνει ο server (status + σώμα με code). Χωρίς status = δίκτυο. */
export type SaveFailure = Error & {
  status?: number;
  data?: { code?: string; error?: string; message?: string; pin_length?: number } | null;
};

interface EmployeeFormProps {
  /** null = νέος υπάλληλος. */
  employee: StoreEmployee | null;
  /** Ό,τι έφερε το /api/roles?for=employee_form (το φίλτρο καταστήματος γίνεται εδώ). */
  roles: RoleOption[];
  store: StoreFlags | null | undefined;
  terminals: PosTerminalOption[];
  /** Ο συνδεδεμένος διαχειριστής ανοίγει τον εαυτό του: ούτε ρόλος ούτε απενεργοποίηση. */
  isSelf: boolean;
  /** POST/PUT· επιστρέφει τον αποθηκευμένο υπάλληλο ή πετά SaveFailure. */
  onSubmit: (body: Record<string, unknown>) => Promise<StoreEmployee | null>;
  onSaved: (saved: StoreEmployee | null, name: string) => void;
  onClose: () => void;
}

const inputCls = 'w-full min-h-[44px] px-3.5 py-2.5 bg-surface-2 border border-line-strong rounded-xl text-t1 placeholder-t4 text-sm '
  + 'focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand';
const legendCls = 'text-sm font-semibold text-t1 mb-3';

/**
 * Ένα πλαϊνό παράθυρο για νέο και για υπάρχοντα υπάλληλο: στοιχεία, ρόλος, PIN, «Ενεργός» και
 * δικαιώματα φεύγουν ΜΑΖΙ με ένα «Αποθήκευση». Στέλνει μόνο ό,τι άλλαξε (employeeModel.buildPayload).
 */
const EmployeeForm: React.FC<EmployeeFormProps> = ({ employee, roles, store, terminals, isSelf, onSubmit, onSaved, onClose }) => {
  const { t } = useTranslation(['employees', 'common']);
  const uid = useId();
  const initial = useMemo(() => initialDraft(employee, roles), [employee, roles]);
  const [draft, setDraft] = useState<EmployeeDraft>(initial);
  const [attempted, setAttempted] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const firstNameRef = useRef<HTMLInputElement>(null);
  const pinRef = useRef<HTMLInputElement>(null);

  const ctx = useMemo(() => ({ employee, roles, store }), [employee, roles, store]);
  const view = permissionView(draft, ctx);
  const { body, dirty, errors, pinRequired, pinDigits } = buildPayload(draft, ctx);
  const isCreate = !employee;
  const choices = offeredRoles(roles, store);
  const currentOffered = isCreate || choices.some(r => r.id === employee!.role_id);
  const legacy = !!employee && isLegacyRole(employee.role_name);
  const roleChanged = !!employee && draft.role_id !== employee.role_id;
  const configurable = PERMISSION_ROLES.includes(view.roleName);
  const unsaved = isCreate ? JSON.stringify(draft) !== JSON.stringify(initial) : dirty;
  const roleLabel = (name: string, fallback?: string) => t(`employees:roles.${name}`, { defaultValue: fallback || name });

  const change = (patch: Partial<EmployeeDraft>) => { setError(null); setDraft(d => ({ ...d, ...patch })); };
  const touch = (field: string) => setTouched(prev => (prev[field] ? prev : { ...prev, [field]: true }));
  // Άλλος ρόλος: τα δικαιώματα ξεκινούν από τις προεπιλογές του (employeeModel.permissionBase).
  const selectRole = (role: RoleOption) => change({ role_id: role.id, permEdits: {}, permsReset: false });
  const toggle = (def: PermissionDef, next: boolean) => {
    setError(null);
    setDraft(d => ({ ...d, permEdits: { ...d.permEdits, [def.key]: next } }));
  };

  const fieldError = (field: 'first_name' | 'pin' | 'role'): string | null => {
    const e = errors[field];
    if (!e || !(attempted || touched[field])) return null;
    return t(`employees:errors.${e.key}`, { digits: 'digits' in e ? e.digits : undefined });
  };

  const messageFor = (err: SaveFailure | null | undefined): string => {
    if (!err || err.status === undefined) return t('employees:errors.network');
    const data = err.data || {};
    const key = errorKeyForCode(data.code);
    if (key) return t(`employees:errors.${key}`, { digits: data.pin_length || pinDigits });
    if (err.status === 403) return (data.code && data.error) || t('employees:errors.forbidden');
    if (err.status === 404) return t('employees:errors.notFound');
    if (err.status >= 500) return t('employees:errors.saveFailed');
    return data.message || data.error || t('employees:errors.saveFailed');
  };

  const requestClose = () => {
    if (saving) return;
    if (unsaved) setConfirmDiscard(true);
    else onClose();
  };

  // Esc κλείνει (με την ίδια ερώτηση αν υπάρχουν αλλαγές)· πρώτα το παράθυρο επιβεβαίωσης.
  const closeRef = useRef(requestClose);
  closeRef.current = requestClose;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (confirmDiscard) setConfirmDiscard(false);
      else closeRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [confirmDiscard]);

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setAttempted(true);
    if (errors.first_name) { firstNameRef.current?.focus(); return; }
    if (errors.pin) { pinRef.current?.focus(); return; }
    if (errors.role || saving || (!isCreate && !dirty)) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await onSubmit(body);
      onSaved(saved, `${draft.first_name.trim()} ${draft.last_name.trim()}`.trim());
    } catch (err) {
      setError(messageFor(err as SaveFailure));
      setSaving(false);
    }
  };

  const titleId = `${uid}-title`;
  const pinHintId = `${uid}-pin-hint`;
  const activeLabelId = `${uid}-active`;
  const firstNameError = fieldError('first_name');
  const pinError = fieldError('pin');
  const roleError = fieldError('role');

  // Με αλλαγή ρόλου το μήκος το λέει ήδη η υπόδειξη του πεδίου (pinRoleChangeHint).
  const pinStatus = employee && (
    !employee.has_pin
      ? { warn: true, text: t('employees:form.pinNone') }
      : !roleChanged && pinMismatch(employee)
        ? { warn: true, text: t('employees:form.pinMismatch', { actual: currentPinLength(employee), expected: pinLengthFor(employee.role_name) }) }
        : { warn: false, text: t('employees:form.pinHas', { digits: currentPinLength(employee) }) }
  );
  const pinHint = isCreate
    ? t('employees:form.pinCreateHint')
    : pinRequired ? t('employees:form.pinRoleChangeHint', { digits: pinDigits }) : t('employees:form.pinKeepHint');

  const canReset = !!employee && configurable && !view.fresh && !!employee.has_custom_permissions;
  let permNote: string;
  if (view.roleName === 'store_admin') permNote = t('employees:permissions.adminAll');
  else if (configurable) {
    permNote = draft.permsReset ? t('employees:permissions.resetPending')
      : (employee && view.fresh) ? t('employees:permissions.roleDefaults')
        : employee?.has_custom_permissions ? t('employees:permissions.custom') : t('employees:permissions.hint');
  } else if (legacy && !roleChanged) permNote = t('employees:permissions.pickRole');
  else permNote = t('employees:permissions.noneForRole');

  return (
    <div className="role-overlay fixed inset-0 z-50 flex justify-end"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.55)', backdropFilter: 'blur(2px)' }}
      onClick={e => { if (e.target === e.currentTarget) requestClose(); }}>
      <div role="dialog" aria-modal="true" aria-labelledby={titleId}
        className="role-dialog h-full w-full sm:max-w-2xl bg-surface-1 border-l border-line shadow-2xl flex flex-col">
        <header className="role-dialog-header flex items-start justify-between gap-3 px-5 py-4 border-b border-line">
          <div className="min-w-0">
            <h2 id={titleId} className="text-lg font-bold text-t1">
              {isCreate ? t('employees:form.addTitle') : t('employees:form.editTitle')}
            </h2>
            {employee && <p className="text-sm text-t3 truncate">{fullName(employee)}</p>}
          </div>
          <button type="button" onClick={requestClose} aria-label={t('employees:form.close')}
            className="w-10 h-10 shrink-0 rounded-xl text-t3 hover:text-t1 hover:bg-surface-3 flex items-center justify-center">
            <X className="w-5 h-5" />
          </button>
        </header>

        <form id={`${uid}-form`} onSubmit={submit} noValidate className="flex-1 overflow-y-auto px-5 py-5 space-y-7">
          {/* Στοιχεία */}
          <fieldset>
            <legend className={legendCls}>{t('employees:form.details')}</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor={`${uid}-first`} className="block text-sm font-medium text-t2 mb-1.5">{t('employees:form.firstName')}</label>
                <input id={`${uid}-first`} ref={firstNameRef} autoFocus value={draft.first_name} autoComplete="one-time-code"
                  onChange={e => change({ first_name: e.target.value })} onBlur={() => touch('first_name')}
                  aria-invalid={!!firstNameError} aria-describedby={firstNameError ? `${uid}-first-error` : undefined}
                  className={inputCls} />
                {firstNameError && <p id={`${uid}-first-error`} className="text-xs text-red-600 dark:text-red-400 mt-1.5">{firstNameError}</p>}
              </div>
              <div>
                <label htmlFor={`${uid}-last`} className="block text-sm font-medium text-t2 mb-1.5">
                  {t('employees:form.lastName')} <span className="font-normal text-t4">({t('employees:form.optional')})</span>
                </label>
                <input id={`${uid}-last`} value={draft.last_name} autoComplete="one-time-code"
                  onChange={e => change({ last_name: e.target.value })} className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor={`${uid}-phone`} className="block text-sm font-medium text-t2 mb-1.5">
                  {t('employees:form.phone')} <span className="font-normal text-t4">({t('employees:form.optional')})</span>
                </label>
                <input id={`${uid}-phone`} type="tel" inputMode="tel" value={draft.phone} autoComplete="one-time-code"
                  onChange={e => change({ phone: e.target.value })} className={inputCls} />
              </div>
            </div>
          </fieldset>

          {/* Ρόλος */}
          <fieldset>
            <legend className={legendCls}>{t('employees:form.role')}</legend>
            {employee && !currentOffered && (
              <div className="mb-3" data-testid="fixed-role">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="px-2.5 py-1 rounded-lg text-sm font-semibold bg-surface-3 text-t1">
                    {roleLabel(employee.role_name, employee.role_display_name)}
                  </span>
                  {legacy && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-amber-500/15 text-amber-800 dark:text-amber-300">
                      <AlertTriangle className="w-3.5 h-3.5" />{t('employees:legacyRole')}
                    </span>
                  )}
                </div>
                <p className="text-xs text-t3 mt-1.5">{legacy ? t('employees:legacyRoleHint') : t('employees:fixedRoleHint')}</p>
              </div>
            )}
            {(currentOffered || legacy) && (
              <div role="radiogroup" aria-label={t('employees:form.role')} className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {choices.map(role => {
                  const checked = draft.role_id === role.id;
                  const hintId = `${uid}-role-${role.name}-hint`;
                  return (
                    <label key={role.id} className={`relative block ${isSelf ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                      <input type="radio" name={`${uid}-role`} className="peer sr-only" checked={checked} disabled={isSelf}
                        aria-label={roleLabel(role.name, role.display_name)} aria-describedby={hintId}
                        onChange={() => selectRole(role)} />
                      <span className={`flex flex-col gap-0.5 min-h-[56px] h-full px-3 py-2.5 rounded-xl border transition-colors
                        peer-focus-visible:ring-2 peer-focus-visible:ring-brand peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-surface-1
                        ${checked ? 'border-brand bg-brand-soft' : 'border-line bg-surface-2 hover:bg-surface-3'}`}>
                        <span className={`text-sm font-semibold ${checked ? 'text-brand' : 'text-t1'}`}>{roleLabel(role.name, role.display_name)}</span>
                        <span id={hintId} className="text-xs text-t3">{t(`employees:roleHints.${role.name}`, { defaultValue: '' })}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
            {isSelf && <p className="text-xs text-t3 mt-2">{t('employees:ownRoleHint')}</p>}
            {roleError && <p className="text-xs text-red-600 dark:text-red-400 mt-2">{roleError}</p>}

            {view.roleName === 'kiosk' && (
              <div className="mt-4">
                <label htmlFor={`${uid}-kiosk`} className="block text-sm font-medium text-t2 mb-1.5">{t('employees:form.kioskTerminal')}</label>
                <select id={`${uid}-kiosk`} value={draft.kiosk_terminal_id ?? ''} className={inputCls}
                  onChange={e => change({ kiosk_terminal_id: e.target.value ? Number(e.target.value) : null })}>
                  <option value="">{t('employees:form.kioskTerminalNone')}</option>
                  {terminals.map(tm => (
                    <option key={tm.id} value={tm.id}>{tm.terminal_name || tm.terminal_id} ({tm.terminal_id})</option>
                  ))}
                </select>
                <p className="text-xs text-t3 mt-1.5">{t('employees:form.kioskTerminalHint')}</p>
              </div>
            )}
          </fieldset>

          {/* PIN */}
          <fieldset>
            <legend className={legendCls}>{t('employees:columns.pin')}</legend>
            {pinStatus && (
              <p className={`text-xs mb-2 ${pinStatus.warn ? 'text-amber-800 dark:text-amber-300 font-medium' : 'text-t3'}`} data-testid="pin-status">
                {pinStatus.text}
              </p>
            )}
            <label htmlFor={`${uid}-pin`} className="block text-sm font-medium text-t2 mb-1.5">
              {isCreate ? t('employees:form.pin', { digits: pinDigits }) : t('employees:form.newPin', { digits: pinDigits })}
            </label>
            <input id={`${uid}-pin`} ref={pinRef} type="password" inputMode="numeric" pattern="[0-9]*" autoComplete="one-time-code"
              maxLength={pinDigits} value={draft.pin}
              onChange={e => change({ pin: e.target.value.replace(/\D/g, '') })} onBlur={() => touch('pin')}
              aria-invalid={!!pinError} aria-describedby={pinHintId}
              className={`${inputCls} sm:max-w-[14rem] tracking-[0.3em]`} />
            <p id={pinHintId} className={`text-xs mt-1.5 ${pinError ? 'text-red-600 dark:text-red-400' : 'text-t3'}`}>{pinError || pinHint}</p>
          </fieldset>

          {/* Κατάσταση */}
          {employee && (
            <fieldset>
              <legend className={legendCls}>{t('employees:form.status')}</legend>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <span id={activeLabelId} className="text-sm font-semibold text-t1">{t('employees:form.active')}</span>
                  <p className="text-xs text-t3 mt-0.5">
                    {isSelf ? t('employees:form.selfActiveHint') : draft.is_active ? t('employees:form.activeHint') : t('employees:form.inactiveHint')}
                  </p>
                </div>
                <button type="button" role="switch" aria-checked={draft.is_active} aria-labelledby={activeLabelId} disabled={isSelf}
                  onClick={() => change({ is_active: !draft.is_active })}
                  className={`relative shrink-0 w-11 h-6 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${draft.is_active ? 'bg-brand' : 'bg-surface-4 dark:bg-surface-3'}`}>
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${draft.is_active ? 'translate-x-5' : ''}`} />
                </button>
              </div>
            </fieldset>
          )}

          {/* Δικαιώματα */}
          <section aria-labelledby={`${uid}-perm-title`}>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
              <h3 id={`${uid}-perm-title`} className="text-sm font-semibold text-t1 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-brand" />{t('employees:permissions.title')}
              </h3>
              {canReset && (
                <button type="button" onClick={() => change({ permsReset: true, permEdits: {} })}
                  className="inline-flex items-center gap-1.5 min-h-[36px] px-2.5 rounded-lg text-xs font-medium text-brand hover:bg-brand-soft">
                  <RotateCcw className="w-3.5 h-3.5" />{t('employees:permissions.reset')}
                </button>
              )}
            </div>
            <p className="text-xs text-t3 mb-3">{permNote}</p>
            {configurable && <EmployeePermissions sections={view.sections} isOn={view.isOn} onToggle={toggle} />}
          </section>
        </form>

        <footer className="border-t border-line px-5 py-4 bg-surface-1">
          {error ? (
            <p role="alert" className="text-sm font-medium text-red-600 dark:text-red-400 mb-3">{error}</p>
          ) : (
            <p role="status" className="text-xs text-t3 mb-3">
              {isCreate ? t('employees:form.createHint') : dirty ? t('employees:form.unsaved') : t('employees:form.noChanges')}
            </p>
          )}
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={requestClose} disabled={saving}
              className="min-h-[44px] px-4 rounded-xl text-sm font-medium border border-line-strong bg-surface-2 text-t1 hover:bg-surface-3 disabled:opacity-50">
              {t('employees:form.cancel')}
            </button>
            <button type="submit" form={`${uid}-form`} disabled={saving || (!isCreate && !dirty)} aria-busy={saving}
              className="inline-flex items-center justify-center gap-2 min-h-[44px] px-5 rounded-xl text-sm font-semibold bg-brand text-brand-fg hover:brightness-110 active:scale-[0.98] transition disabled:opacity-50 disabled:cursor-not-allowed">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {saving ? t('employees:form.saving') : isCreate ? t('employees:form.create') : t('employees:form.save')}
            </button>
          </div>
        </footer>
      </div>

      <ConfirmModal
        isOpen={confirmDiscard}
        title={t('employees:form.discardTitle')}
        message={t('employees:form.discardMessage')}
        confirmLabel={t('employees:form.discardConfirm')}
        cancelLabel={t('employees:form.discardCancel')}
        variant="warning"
        onConfirm={() => { setConfirmDiscard(false); onClose(); }}
        onCancel={() => setConfirmDiscard(false)}
      />
    </div>
  );
};

export default EmployeeForm;
